import React, { useState } from "react";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Compass, Users, UserCircle, CreditCard, Hotel, Truck, Calendar,
  Building2, TrendingUp, FileCheck, BellRing, BarChart3, Plane, LogOut, Menu, X, Moon, Sun, Sparkles, MountainSnow, Megaphone
} from "lucide-react";

const ALL_NAV = [
  { key: "dashboard", to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin","operations","guide","sales","accounting"] },
  { key: "tours", to: "/tours", label: "Expediciones", icon: Compass, roles: ["admin","operations","guide","sales"] },
  { key: "clients", to: "/clients", label: "Clientes", icon: UserCircle, roles: ["admin","operations","sales"] },
  { key: "guides", to: "/guides", label: "Guías", icon: MountainSnow, roles: ["admin","operations","sales"] },
  { key: "participants", to: "/participants", label: "Reservas por tour", icon: Users, roles: ["admin","operations","sales"] },
  { key: "payments", to: "/payments", label: "Pagos", icon: CreditCard, roles: ["admin","operations","accounting"] },
  { key: "accommodations", to: "/accommodations", label: "Hoteles", icon: Hotel, roles: ["admin","operations","guide"] },
  { key: "transport", to: "/transport", label: "Transporte", icon: Truck, roles: ["admin","operations","guide"] },
  { key: "itinerary", to: "/itinerary", label: "Itinerario", icon: Calendar, roles: ["admin","operations","guide","client"] },
  { key: "finances", to: "/finances", label: "Gastos & Rentabilidad", icon: TrendingUp, roles: ["admin","accounting"] },
  { key: "tasks", to: "/tasks", label: "Tareas & Alertas", icon: BellRing, roles: ["admin","operations","guide","sales"] },
  { key: "reports", to: "/reports", label: "Informes", icon: BarChart3, roles: ["admin","accounting","operations"] },
  { key: "marketing", to: "/marketing", label: "Marketing", icon: Megaphone, roles: ["admin","marketing"] },
  { key: "portal", to: "/portal", label: "Mi Expedición", icon: Plane, roles: ["client"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  const navigate = useNavigate();

  React.useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  if (!user) return null;

  const nav = ALL_NAV.filter((n) => n.roles.includes(user.role));

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0B0F17] text-slate-900 dark:text-slate-100">
      {/* Mobile top bar */}
      <div className="lg:hidden flex items-center justify-between h-14 px-4 border-b border-slate-200 dark:border-[#25334D] bg-white dark:bg-[#131B2A] sticky top-0 z-40">
        <div className="flex items-center gap-2">
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-[#1A2336]">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
          <span className="font-semibold tracking-tight">IceMonkey</span>
        </div>
        <button data-testid="theme-toggle-mobile" onClick={() => setDark(!dark)} className="p-2 rounded-md">
          {dark ? <Sun size={18} /> : <Moon size={18} />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky lg:top-0 top-14 lg:h-screen h-[calc(100vh-3.5rem)] w-72 z-30 bg-white dark:bg-[#131B2A] border-r border-slate-200 dark:border-[#25334D] transition-transform duration-200 overflow-y-auto`}
        >
          <div className="hidden lg:flex items-center h-20 px-6 border-b border-slate-200 dark:border-[#25334D]">
            <img
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_travel-ops-central/artifacts/jrl28zc2_logo%20fondo%20blanco%20solo%20letras.png"
              alt="IceMonkey Photo Expeditions"
              className="h-9 w-auto dark:invert dark:brightness-100"
              data-testid="brand-logo"
            />
          </div>
          <nav className="p-3 space-y-1">
            {nav.map((n) => (
              <NavLink
                key={n.key}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-item-${n.key}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-[#026FCB] text-white shadow-sm"
                      : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-[#1A2336]"
                  }`
                }
              >
                <n.icon size={18} />
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-slate-200 dark:border-[#25334D] mt-2">
            <div className="text-xs text-slate-500 mb-2 mono uppercase tracking-wider">Sesión</div>
            <div className="text-sm font-medium truncate">{user.name}</div>
            <div className="text-xs text-slate-500 truncate">{user.email}</div>
            <div className="mt-1 text-[10px] uppercase tracking-widest text-[#026FCB] mono">{user.role}</div>
            <Button data-testid="logout-button" variant="outline" size="sm" className="w-full mt-3" onClick={async () => { await logout(); navigate("/login"); }}>
              <LogOut size={14} className="mr-2" /> Cerrar sesión
            </Button>
            <ChangePasswordDialog />
          </div>
        </aside>

        {open && <div className="lg:hidden fixed inset-0 z-20 bg-black/40" onClick={() => setOpen(false)} />}

        {/* Main */}
        <main className="flex-1 min-w-0 overflow-x-hidden">
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-slate-200 dark:border-[#25334D] bg-white/70 dark:bg-[#131B2A]/70 backdrop-blur-md sticky top-0 z-20">
            <div />
            <div className="flex items-center gap-2">
              <button data-testid="theme-toggle" onClick={() => setDark(!dark)} className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-[#1A2336]">
                {dark ? <Sun size={18} /> : <Moon size={18} />}
              </button>
            </div>
          </div>
          <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto space-y-6 min-w-0 overflow-x-hidden">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
